import React, { useState, useEffect, useRef } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
// import '../styles/CalenderStyles.css';

// Custom CSS for scrollable calendar
const styles = {
  container: {
    width: '100%',
    maxWidth: '800px',
    height: '500px',
    padding: '12px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    margin: '0'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1rem',
    cursor: 'pointer',
    padding: '4px 8px'
  },
  calendarContainer: {
    flex: 1,
    overflow: 'auto'
  },
  scrollableMonths: {
    minHeight: '1500px', // Height for multiple months
    position: 'relative'
  },
  eventIndicator: {
    color: '#3b82f6',
    fontSize: 12,
    flexWrap: 'wrap',
    wordBreak: 'break-word', // Add this to break long words
    overflowWrap: 'break-word', // Add this for better wrapping
    whiteSpace: 'normal', // Add this to allow text to wrap to the next line
    maxWidth: '100%' // Ensure it doesn't overflow its container
  },
  eventDay: {
    backgroundColor: '#eff6ff'
  },
  monthHeader: {
    textAlign: 'center',
    padding: '10px 0',
    fontWeight: 'bold',
    backgroundColor: '#f8f9fa',
    position: 'sticky',
    top: 0,
    zIndex: 10
  }
};

const localizer = momentLocalizer(moment);

export default function ScrollablePopupCalendar({ onClose = () => {}, events: eventsData = [] }) {
  const [events, setEvents] = useState([]);
  const [visibleMonths, setVisibleMonths] = useState([]);
  const calendarRef = useRef(null);

  // Convert Unix timestamps to JavaScript Date objects
  useEffect(() => {
    const formattedEvents = eventsData.map(event => {
      // Create date in UTC to avoid timezone issues
      const timestamp = parseInt(event.date) * 1000;
      const date = new Date(timestamp);
      
      // Create a date at midnight in local timezone
      const localDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
      
      return {
        id: event.id,
        title: event.title || event.name,
        description: event.description,
        type: event.type,
        location: event.location,
        image: event.image,
        start: localDate,
        end: localDate,
        allDay: true
      };
    });
    
    setEvents(formattedEvents);
  }, [eventsData]);

  // Generate the visible months (past 3 and future 9 months)
  useEffect(() => {
    const currentDate = moment();
    const months = [];
    
    // Add past 3 months
    for (let i = -3; i <= 9; i++) {
      const monthDate = moment(currentDate).add(i, 'months');
      months.push({
        date: monthDate.toDate(),
        label: monthDate.format('MMMM YYYY')
      });
    }
    
    setVisibleMonths(months);
  }, []);

  // Custom event component that just shows a dot indicator
  const EventIndicator = ({ event }) => {
    return (
      <div style={styles.eventIndicator}>{event.title}</div>
    );
  };

  // Custom day cell renderer to show event indicators
  const dayPropGetter = (date) => {
    const hasEvent = events.some(event => 
      moment(event.start).isSame(date, 'day')
    );
    
    return {
      style: hasEvent ? {...styles.eventDay, margin: 0, padding: 0} : {margin: 0, padding: 0}
    };
  };

  // Handle day click to potentially show event details
  const handleSelectSlot = (slotInfo) => {
    const selectedDate = moment(slotInfo.start).startOf('day');
    
    // Find events on the selected day
    const eventsOnDay = events.filter(event => 
      moment(event.start).isSame(selectedDate, 'day')
    );
    
    if (eventsOnDay.length > 0) {
      console.log('Events on selected day:', eventsOnDay);
      // Here you could open a secondary popup with event details,
      // or pass these events to a parent component
    }
  };

  // Scroll to current month when component mounts
  useEffect(() => {
    if (calendarRef.current) {
      const currentMonthElement = document.getElementById('current-month');
      if (currentMonthElement) {
        setTimeout(() => {
          currentMonthElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [visibleMonths]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Events Calendar</h2>
        <button style={styles.closeButton} onClick={onClose}>×</button>
      </div>
      <div style={styles.calendarContainer} ref={calendarRef}>
        <div style={styles.scrollableMonths}>
          {visibleMonths.map((month, index) => {
            const isCurrent = moment(month.date).isSame(moment(), 'month');
            return (
              <div key={month.label} id={isCurrent ? 'current-month' : `month-${index}`}>
                <div style={styles.monthHeader}>
                  {month.label}
                </div>
                <div style={{ height: '310px' }}>
                  <Calendar
                    localizer={localizer}
                    events={events}
                    date={month.date}
                    views={['month']}
                    view="month"
                    onNavigate={() => {}} // Disable navigation
                    toolbar={false} // Hide toolbar
                    dayPropGetter={dayPropGetter}
                    components={{
                      event: EventIndicator
                    }}
                    eventPropGetter={(event) => ({
                      style: {
                        backgroundColor: 'transparent',
                        border: 'none',
                      }
                    })}
                    onSelectSlot={handleSelectSlot}
                    selectable
                    popup
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}